import { useEffect, useRef, useState } from 'react';

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, useDisclosure, useToast } from '@chakra-ui/react';
import { Formik, Field, Form } from 'formik';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';

import ArrowIcon from '@/assets/ArrowIcon';
import { COLORS, DURATION_TOAST } from '@/common/constants';
import TablePrivilege from '@/components/Tables/TablePrivilege';
import SelectRole from '@/components/Selects/SelectRole';
import ModalConfirmation from '@/components/Modals/ModalConfirmation';
import { useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import { addPrivilegeSchema } from '@/schemas/addPrivilegeSchema';
import { getRoleData } from '@/services/roleService';
import { getDataPrivilegeByRole, postAssignPrivilegeToRole } from '@/services/privilegeService';
import { getAssignedPrivileges, getRevokedPrivileges, listInitialPrivileges } from '@/utils/privileges';

const DEFAULT_ROLE = 'superadmin';

const EditPrivilege = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const tablePrivilegeRef = useRef() as any;
    const toast = useToast();
    const navigate = useNavigate();
    const name = useAppSelector((state: RootState) => state.account.name);

    const [listRole, setListRole] = useState([]) as any;
    const [listPrivilege, setListPrivilege] = useState([]) as any;
    const [roleId, setRoleId] = useState(DEFAULT_ROLE);
    const [data, setData] = useState(null) as any;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [initiallyAllowed, setInitiallyAllowed] = useState<string[]>([]);
    const [initiallDenied, setInitiallyDenied] = useState<string[]>([]);

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleSubmit = () => {
        setTitle("Update This Privilege?");
        setConfirmationTest("Are you sure you want to update?");
        setConfirmationTitleButton("Update Privilege");
        onOpen();
        setIsSubmitting(true);
        if (tablePrivilegeRef.current) {
            const checkedPrivileges = tablePrivilegeRef.current.getCheckedPrivileges();
            const uncheckedPrivileges = tablePrivilegeRef.current.getUncheckedPrivileges();
            const assignedPrivileges = getAssignedPrivileges(checkedPrivileges, initiallDenied);
            const revokedPrivileges = getRevokedPrivileges(uncheckedPrivileges, initiallyAllowed);
            const obj = {
                assignPrivileges: assignedPrivileges,
                revokePrivileges: revokedPrivileges,
                roleId,
                createdBy: name
            };
            setData(obj);
        }
    }

    const handleChangeRole = (value: string, props: any) => {
        props.setFieldValue('role', value);
        setRoleId(value);
    }

    const handleConfirm = async () => {
        onClose();
        const response = await postAssignPrivilegeToRole(data) as any;
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'User updated.',
                description: "We've updated this privilege.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigate('/user-management/manage-privilege');
            }, 1000)
        } else {
            toast({
                title: 'Privilege failed to update.',
                description: "We unabled to update this privilege.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const getListRole = async () => {
        const response = await getRoleData() as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedOptions = response.data.map((item: any) => ({ value: item.roleId, label: item.roleName }));
            setListRole(formattedOptions);
        }
    }

    const getPrivilege = async () => {
        const response = await getDataPrivilegeByRole(roleId) as any;
        if (response.status === HttpStatusCode.Ok) {
            setListPrivilege(response.data);
            const privilegeList = listInitialPrivileges(response.data);
            setInitiallyAllowed(privilegeList.allowed);
            setInitiallyDenied(privilegeList.denied);
        }
    }

    useEffect(() => {
        getListRole();
    }, []);

    useEffect(() => {
        getPrivilege();
    }, [roleId]);

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            <div className="form-container">
                <Formik
                    initialValues={{
                        role: DEFAULT_ROLE
                    }}
                    validationSchema={addPrivilegeSchema}
                    onSubmit={() => {
                        handleSubmit();
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form>
                            <Field name="role">
                                {({ form }: any) => (
                                    <FormControl
                                        isInvalid={form.errors.role && form.touched.role}
                                        style={{ marginBottom: 15 }}
                                    >
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">
                                                Role<span style={{ color: "red" }}>*</span>
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <SelectRole
                                                    name="role"
                                                    options={listRole}
                                                    handleChange={({ value }: any) => handleChangeRole(value, props)}
                                                    value={props.values.role}
                                                    defaultValue={props.values.role}
                                                    hasError={form.errors.role && form.touched.role}
                                                    style={{ width: "100%", marginRight: "8px" }}
                                                />
                                                <FormErrorMessage>
                                                    {form.errors.role}
                                                </FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Divider mb={5} />
                            <TablePrivilege
                                ref={tablePrivilegeRef}
                                initialDataList={listPrivilege}
                                roleId={roleId}
                                isLoading={false}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                {/* <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button> */}
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    marginRight={5}
                                    isLoading={isSubmitting}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Save
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default EditPrivilege;
